/**
 * English: URL-scoped org + station for /agent-management (decoupled from chat org picker).
 * Thai: org + station จาก URL สำหรับ /agent-management (แยกจากตัวเลือก org แชท)
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, ENDPOINTS } from '../config/api';
import { parseJsonResponse } from '../utils/httpJson';
import { resolveChatOrgId } from '../utils/resolveChatOrgId';
import type { Station } from '../types';

export type EnabledOrg = { org_name: string; display_name: string };

function normalizeInventoryStation(raw: Record<string, unknown>): Station {
  const slug = String(raw.station_id ?? '').trim();
  const dbId = String(raw.station_db_id ?? '').trim();
  const id = slug || dbId;
  const displayName = raw.name != null ? String(raw.name).trim() : '';
  return {
    ...raw,
    id,
    name: displayName || slug || id,
  };
}

function stationOrgName(station: Station): string {
  return String((station as Station & { org_name?: string }).org_name ?? '').trim();
}

function orgNamesMatch(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

export function agentManagementQuery(org?: string, station?: string): string {
  const params = new URLSearchParams();
  const o = (org ?? '').trim();
  const s = (station ?? '').trim();
  if (o) params.set('org', o);
  if (s) params.set('station', s);
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

function useEnabledOrganizations() {
  const { token } = useAuth();
  const [orgs, setOrgs] = useState<EnabledOrg[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const headers: Record<string, string> = { Accept: 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(ENDPOINTS.adminOrganizations, {
        credentials: 'include',
        headers,
      });
      const data = (await res.json().catch(() => ({}))) as { organizations?: EnabledOrg[] };
      if (res.ok) setOrgs(data.organizations || []);
      else setOrgs([]);
    } catch {
      setOrgs([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { orgs, loading, refresh };
}

function useAllStations() {
  const { token } = useAuth();
  const [stations, setStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URL(`${API_BASE}${ENDPOINTS.stations}`, window.location.origin);
      const headers: Record<string, string> = {};
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(url.toString(), { headers });
      const data = await parseJsonResponse<{ stations?: Station[] }>(res, {
        url: url.toString(),
      });
      if (!res.ok) {
        setStations([]);
        return;
      }
      const rawList = data.stations || [];
      const list = rawList.map((s) => normalizeInventoryStation(s as Record<string, unknown>));
      const seen = new Set<string>();
      const deduped = list.filter((s) => {
        const id = (s.id || '').trim();
        if (!id || seen.has(id)) return false;
        seen.add(id);
        return true;
      });
      setStations(deduped);
    } catch {
      setStations([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return { stations, loading, refresh };
}

export function useAgentManagementScope() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    orgs: enabledOrgs,
    loading: orgsLoading,
    refresh: refreshOrgs,
  } = useEnabledOrganizations();
  const {
    stations: allStations,
    loading: allStationsLoading,
    refresh: refreshStations,
  } = useAllStations();

  const orgName = (searchParams.get('org') ?? '').trim();
  const stationId = (searchParams.get('station') ?? '').trim();

  const orgStations = useMemo(() => {
    if (!orgName) return [];
    return allStations.filter((s) => orgNamesMatch(stationOrgName(s), orgName));
  }, [allStations, orgName]);

  const selectedStation = useMemo(
    () => allStations.find((s) => s.id === stationId) ?? null,
    [allStations, stationId],
  );

  const resolvedOrgId = useMemo(
    () =>
      resolveChatOrgId({
        userOrgName: orgName,
        activeStationId: stationId,
        stationOrgName: selectedStation ? stationOrgName(selectedStation) : '',
      }),
    [orgName, stationId, selectedStation],
  );

  const setScope = useCallback(
    (next: { org?: string; station?: string }) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next.org !== undefined) {
            const v = next.org.trim();
            if (v) params.set('org', v);
            else params.delete('org');
          }
          if (next.station !== undefined) {
            const v = next.station.trim();
            if (v) params.set('station', v);
            else params.delete('station');
          }
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setOrgName = useCallback(
    async (name: string): Promise<{ ok: true } | { ok: false; error: string }> => {
      const trimmed = name.trim();
      setScope({ org: trimmed, station: '' });
      return { ok: true };
    },
    [setScope],
  );

  const setStationId = useCallback(
    (id: string) => {
      setScope({ station: id });
    },
    [setScope],
  );

  const ensureDefaultOrg = useCallback(() => {
    if (orgName || enabledOrgs.length === 0) return;
    setScope({ org: enabledOrgs[0].org_name });
  }, [orgName, enabledOrgs, setScope]);

  useEffect(() => {
    if (!orgName || allStationsLoading) return;
    if (orgStations.length === 0) {
      if (stationId) setScope({ station: '' });
      return;
    }
    const currentInOrg = orgStations.some((s) => s.id === stationId);
    if (!currentInOrg) {
      setScope({ station: orgStations[0].id });
    }
  }, [orgName, orgStations, stationId, allStationsLoading, setScope]);

  return {
    orgName,
    stationId,
    orgId: resolvedOrgId,
    resolvedOrgId,
    setOrgName,
    setStationId,
    enabledOrgs,
    orgStations,
    allStationsLoading,
    stationsLoading: allStationsLoading,
    orgsLoading,
    ensureDefaultOrg,
    refreshOrgs,
    refreshStations,
  };
}
